import type { ReactNode } from 'react';

/**
 * Building blocks for the guide.
 *
 * The pictures of each screen are drawn with the dashboard's own CSS classes
 * rather than pasted in as screenshots: a picture then cannot drift from the
 * real styling, cannot leak a real balance or key onto a public page, and
 * prints sharply at any size.
 */

/** A numbered callout ringed around one feature of a picture. */
export function Spot({ n, children, block = false }: { n: number; children: ReactNode; block?: boolean }) {
  const Tag = block ? 'div' : 'span';
  return (
    <Tag className={block ? 'spot spot-block' : 'spot'}>
      <span className="spot-n">{n}</span>
      {children}
    </Tag>
  );
}

/**
 * A picture of a screen, framed as a browser window. `inert` keeps the drawn
 * buttons and inputs from being clicked, focused or read out as real controls;
 * the label describes the picture instead.
 */
export function Shot({ label, caption, children }: { label: string; caption?: ReactNode; children: ReactNode }) {
  return (
    <figure className="shot mock" aria-label={label}>
      <div className="shot-bar" aria-hidden="true">
        <span className="shot-dots">
          <i />
          <i />
          <i />
        </span>
        <span className="shot-url">{window.location.host}</span>
        <span className="shot-tag">example picture</span>
      </div>
      <div className="shot-body">
        <div className="shot-screen" inert>
          {children}
        </div>
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

export function Chapter({
  id,
  n,
  title,
  purpose,
  children,
}: {
  id: string;
  n: number;
  title: string;
  purpose: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="chapter" id={id}>
      <div className="chapter-head">
        <span className="chapter-n">{n}</span>
        <div>
          <h2>{title}</h2>
          <p>{purpose}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

export interface Task {
  title: string;
  steps: ReactNode[];
  note?: ReactNode;
  /** Points that are not a sequence — shown as bullets, not numbered steps. */
  bullets?: boolean;
}

/**
 * The two columns under a picture. Legend entries are numbered by position,
 * so their order must match the Spot numbers in the picture above.
 */
export function Explain({ legend, tasks }: { legend: Array<[string, ReactNode]>; tasks: Task[] }) {
  return (
    <div className="explain">
      <div>
        <h3>What you see</h3>
        <ol className="legend">
          {legend.map(([name, text], i) => (
            <li key={name}>
              <span className="spot-n">{i + 1}</span>
              <span>
                <b>{name}</b> — {text}
              </span>
            </li>
          ))}
        </ol>
      </div>
      <div>
        <h3>How to do it</h3>
        {tasks.map((task) => {
          const List = task.bullets ? 'ul' : 'ol';
          return (
            <div className="howto" key={task.title}>
              <h4>{task.title}</h4>
              <List>
                {task.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </List>
              {task.note && <p className="howto-note">{task.note}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Points({ items }: { items: ReactNode[] }) {
  return (
    <ul className="guide-points">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}
